import { useState, useEffect, useRef, useCallback } from 'react';
import { getDocument, updateDocument, Document } from '../../../api/documentApi';
import { Conflict, canEdit, conflictFrom } from '../../../api/sharingApi';
import { useLanguage } from '../../../i18n/LanguageContext';

interface UseDocumentDataProps {
    documentId: number;
}

export const useDocumentData = ({ documentId }: UseDocumentDataProps) => {
    // A hook runs inside a component, so the language pack is reachable here;
    // the two failure messages below are rendered by DocumentEditor.
    const { t } = useLanguage();
    const [documentData, setDocumentData] = useState<Document | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const [error, setError] = useState<string>('');
    const [contentLoaded, setContentLoaded] = useState(false);
    // Someone else saved over the version this editor started from. Until the
    // writer says what to do about it, nothing here writes to the server.
    const [conflict, setConflict] = useState<Conflict | null>(null);

    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastContentRef = useRef<string>('');
    const initialContentRef = useRef<string>('');
    const isAutoSavingRef = useRef<boolean>(false);
    // The updated_at this editor last saw, sent with every save so the server
    // can tell "I have read what is there" from "I never saw it".
    const baseUpdatedAtRef = useRef<string | null>(null);
    const conflictRef = useRef(false);

    const loadDocument = useCallback(async () => {
        setIsLoading(true);
        setError('');

        try {
            const doc = await getDocument(documentId);
            setDocumentData(doc);
            baseUpdatedAtRef.current = doc.updated_at;
            setContentLoaded(true);
        } catch (error: any) {
            console.error('Failed to load document:', error);
            setError(error.response?.data?.error || t.editor.loadFailed);
        } finally {
            setIsLoading(false);
        }
    }, [documentId, t]);

    const cancelPendingSave = useCallback(() => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = null;
        }
    }, []);

    const autoSave = useCallback(async (content: string) => {
        if (!documentData || isSaving || conflictRef.current) return;
        // Viewers have nothing to save, and the server would answer 403
        if (!canEdit(documentData.access)) return;

        // Don't save if content hasn't changed from the initial loaded content
        if (content === initialContentRef.current) {
            return;
        }


        // Set flag to prevent content reset during autosave
        isAutoSavingRef.current = true;
        setIsSaving(true);

        try {
            const saved = await updateDocument(documentId, {
                ...documentData,
                content: content,
                ...(baseUpdatedAtRef.current ? { base_updated_at: baseUpdatedAtRef.current } : {}),
            });

            // What we just wrote is now the version to measure the next save
            // against — without this, the second save of a session conflicts
            // with the first.
            if (saved?.updated_at) baseUpdatedAtRef.current = saved.updated_at;
            setLastSaved(new Date());
        } catch (error: any) {
            const clash = conflictFrom(error);
            if (clash) {
                // Stop autosaving: every further attempt would hit the same
                // wall, and the writer's text stays safe in the editor.
                conflictRef.current = true;
                cancelPendingSave();
                setConflict(clash);
            } else {
                console.error('Autosave failed:', error);
                setError(t.editor.saveFailed);
            }
        } finally {
            setIsSaving(false);
            // Small delay to ensure UI updates before clearing flag
            setTimeout(() => {
                isAutoSavingRef.current = false;
            }, 100);
        }
    }, [documentData, isSaving, documentId, cancelPendingSave, t]);

    const scheduleAutoSave = useCallback((content: string) => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
            autoSave(content);
        }, 2000);
    }, [autoSave]);

    // Save on demand (Ctrl/Cmd+S). Drops the pending debounce first so the
    // two writes can't race, then goes through the same path as autosave.
    const saveNow = useCallback((content: string) => {
        cancelPendingSave();
        return autoSave(content);
    }, [autoSave, cancelPendingSave]);

    /** Their version wins: the caller has put it in the editor. */
    const acceptTheirVersion = useCallback(() => {
        if (conflict) baseUpdatedAtRef.current = conflict.updated_at;
        conflictRef.current = false;
        setConflict(null);
    }, [conflict]);

    /** Mine wins: save again, now based on the version they wrote. */
    const keepMyVersion = useCallback(async (content: string) => {
        if (conflict) baseUpdatedAtRef.current = conflict.updated_at;
        conflictRef.current = false;
        setConflict(null);
        await autoSave(content);
    }, [conflict, autoSave]);

    /** After a restore or any other write that moved the document on. */
    const setSaveBase = useCallback((updatedAt: string) => {
        baseUpdatedAtRef.current = updatedAt;
    }, []);

    useEffect(() => {
        loadDocument();
    }, [loadDocument]);

    return {
        documentData,
        setDocumentData,
        isLoading,
        isSaving,
        lastSaved,
        error,
        contentLoaded,
        conflict,
        acceptTheirVersion,
        keepMyVersion,
        setSaveBase,
        autoSave,
        saveNow,
        cancelPendingSave,
        scheduleAutoSave,
        lastContentRef,
        initialContentRef,
        isAutoSavingRef,
        loadDocument,
    };
};
